import { Button } from './Button';
import './common.css';

export function Spinner({ large = false, className = '' }) {
  return (
    <span
      className={`spinner ${large ? 'spinner--lg' : ''} ${className}`}
      role="status"
      aria-label="Loading"
    />
  );
}

export function Loading({ label = 'Loading…', large = false }) {
  return (
    <div className="state-block">
      <Spinner large={large} />
      <div className="state-block__body">{label}</div>
    </div>
  );
}

/**
 * Empty states carry an action wherever one exists — an empty screen that only
 * says "nothing here" leaves the user to guess what to do next.
 */
export function EmptyState({ glyph = '◍', title, children, action = null }) {
  return (
    <div className="state-block">
      <div className="state-block__glyph" aria-hidden="true">
        {glyph}
      </div>
      {title && <div className="state-block__title">{title}</div>}
      {children && <div className="state-block__body">{children}</div>}
      {action}
    </div>
  );
}

/**
 * Error states name the failure and, where the error code tells us what to do,
 * say it: start the worker, add version metadata, retry.
 */
export function ErrorState({ error, onRetry = null, title = null }) {
  const code = error?.code;
  const guidance = {
    worker_unavailable:
      'Start the inference worker: `python run_worker.py` in backend/, or `docker compose up worker`.',
    worker_busy: 'The GPU is serialised by design — one request at a time. Try again in a moment.',
    capacity_exceeded: `Every inference slot stayed busy for the whole wait. Try again${
      error?.retryAfter ? ` in ${error.retryAfter} s` : ' in a few seconds'
    }.`,
    model_starting:
      'The base model is loading on the GPU (one to three minutes after a Load). Try again shortly.',
    model_not_loaded:
      'This version’s base model is not on the GPU. An administrator loads it in Settings → Model serving.',
    model_capacity_reached:
      'Every model slot is taken. Unload a model in Settings → Model serving, or raise the limit there.',
    model_server_unavailable:
      'The model server is not reachable: `docker compose ps modelserver` and its logs say why.',
    serving_not_prepared: 'Run the command above once on the server, then load the version again.',
    version_not_ready:
      'Open Settings → Model versions and complete this version’s metadata (attribute set, response scheme, preprocessing).',
    network_error: 'The API is not responding. Check that it is running on port 8000.',
    validation_error: 'The request was rejected. Adjust the inputs and try again.',
  }[code];

  return (
    <div className="state-block state-block--error">
      <div className="state-block__glyph" aria-hidden="true">
        !
      </div>
      <div className="state-block__title">{title || 'Something went wrong'}</div>
      <div className="state-block__body">
        {error?.message || String(error)}
        {guidance && (
          <>
            <br />
            <span className="subtle">{guidance}</span>
          </>
        )}
      </div>
      {onRetry && (
        <Button variant="primary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function Badge({ children, tone = 'default', title }) {
  return (
    <span className={`badge ${tone !== 'default' ? `badge--${tone}` : ''}`} title={title}>
      {children}
    </span>
  );
}

export function ProgressBar({ value = 0, max = 1, danger = false }) {
  const ratio = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuenow={Math.round(ratio * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={`progress__fill ${danger ? 'progress__fill--danger' : ''}`}
        style={{ width: `${ratio * 100}%` }}
      />
    </div>
  );
}

export function ToastHost({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <div className="toast-host" role="region" aria-label="Notifications">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast toast--${toast.tone}`} role="status">
          <div className="row" style={{ alignItems: 'flex-start' }}>
            <div className="grow">
              <div className="toast__title">{toast.title}</div>
              {toast.message && <div className="toast__message">{toast.message}</div>}
            </div>
            <button
              type="button"
              className="btn btn--ghost btn--icon"
              onClick={() => onDismiss(toast.id)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
          {toast.action && (
            <div className="toast__actions">
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  toast.action.onClick();
                  onDismiss(toast.id);
                }}
              >
                {toast.action.label}
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
